import type { PaigeDocumentOrigin, PaigeMobileSender } from './mobile-types';

/**
 * Paige Mobile's words in the workbench (docs/engineering/mobile-capture.md, section 13): a pasted list
 * read into people, a sender's state in one phrase, the field a job most likely wants the sender's name
 * in, and the origin line a reviewer sees.
 */

const ADDRESS = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/;

/**
 * People from what an admin pastes: one per line, or separated by commas or semicolons; each an
 * address or `Name <address>`. What is not an address is named back, never dropped in silence.
 */
export function parsePeople(text: string): {
  people: { email: string; name?: string }[];
  unusable: string[];
} {
  const people: { email: string; name?: string }[] = [];
  const unusable: string[] = [];
  const seen = new Set<string>();
  for (const raw of text.split(/[\n,;]+/)) {
    const entry = raw.trim();
    if (!entry) continue;
    const named = entry.match(/^(.*?)\s*<([^<>]+)>$/);
    const email = (named ? (named[2] ?? '') : entry).trim().toLowerCase();
    const name = named?.[1]?.trim().replace(/^"|"$/g, '');
    if (!ADDRESS.test(email)) unusable.push(entry);
    else if (!seen.has(email)) {
      seen.add(email);
      people.push(name ? { email, name } : { email });
    }
  }
  return { people, unusable };
}

/** A sender's state in a word or two: Invited, Invite expired, Active, Signed out. */
export function senderState(sender: PaigeMobileSender): string {
  if (sender.status === 'expired') return 'Invite expired';
  if (sender.status === 'invited') return 'Invited';
  return sender.phones.length > 0 ? 'Active' : 'Signed out';
}

/** The field a job most likely wants the sender's name in, when it has one and nothing is bound yet. */
export function suggestedSenderField(fields: string[]): string | null {
  const wanted =
    /^(driver|employee|technician|tech|submitted by|sales ?rep|representative|sender|courier|delivered by)( name)?$/i;
  return fields.find((field) => wanted.test(field.trim())) ?? null;
}

/** "Jane Smith", or her address when Paige has no name. */
export function senderName(origin: Pick<PaigeDocumentOrigin, 'sender'>): string {
  return origin.sender.name?.trim() || origin.sender.email;
}

/** When it was captured, in the reader's own time: "Oct 4, 9:12 AM". */
export function capturedWhen(iso: string, locale?: string): string {
  return new Date(iso).toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Where: "27.951, -82.457 (±12 m)". */
export function placeWords(location: NonNullable<PaigeDocumentOrigin['location']>): string {
  const at = `${location.latitude.toFixed(3)}, ${location.longitude.toFixed(3)}`;
  return location.accuracyMeters === null
    ? at
    : `${at} (±${Math.round(location.accuracyMeters)} m)`;
}

/** A map of the place, opened only by the reader's own click, with no referrer. */
export function mapLink(location: NonNullable<PaigeDocumentOrigin['location']>): string {
  return `https://www.openstreetmap.org/?mlat=${location.latitude}&mlon=${location.longitude}#map=17/${location.latitude}/${location.longitude}`;
}

/** Why the sender was asked to scan it again. */
export function rescanWords(rescan: NonNullable<PaigeDocumentOrigin['rescan']>): string {
  if (rescan.reason === 'reviewer')
    return rescan.note ? `Asked to scan it again: ${rescan.note}` : 'Asked to scan it again';
  if (rescan.reason === 'damaged') return 'The file would not open; asked to scan it again';
  return 'Paige could not read it; asked to scan it again';
}
